// Per-card copy + CTA for the standardized dashboard empty state.
//
// Each entry: { icon, headline, subtext, cta }
//   icon    — 'clock' | 'chart' | 'spark' (rendered by DashboardCardEmptyState)
//   headline— bold line shown when the card has loaded but has no data
//   subtext — one supporting sentence
//   cta     — { label, path } where `path` is the route segment after
//             `/${client}/`, or null when no action applies.
//
// AI-CONTEXT: See docs/features/dashboard-empty-states.md

const SHARE = { label: 'Deel de vragenlijst', path: 'sharequestionnaire' }
// The events page (route `events`, labeled "Activiteiten" in the UI) is where
// output/event results are registered — the right next step when a card is empty.
const TO_EVENTS = { label: 'Vul resultaten in', path: 'events' }
const TO_PERSONAS = { label: 'Naar medewerkers', path: 'personas' }
const TO_HOUSEVISITS = { label: 'Naar huisbezoeken', path: 'housevisitsoverview' }
const MANAGE_PROJECTS = { label: 'Beheer projecten', path: 'projects' }

export const EMPTY_STATE_CONFIG = {
    // ── Analytics cards (DashboardVisualisationCard types) ──
    research: {
        icon: 'clock',
        headline: 'Nog geen onderzoeksresultaten binnen',
        subtext: 'Resultaten verschijnen zodra de eerste metingen zijn ingevuld.',
        cta: SHARE,
    },
    effect: {
        icon: 'clock',
        headline: 'Nog geen resultaten voor dit effect',
        subtext: 'Dit effect wordt gemeten zodra de vragenlijst is ingevuld.',
        cta: SHARE,
    },
    indicator: {
        icon: 'clock',
        headline: 'Nog geen antwoorden op deze vraag',
        subtext: 'Resultaten verschijnen zodra deelnemers antwoorden.',
        cta: SHARE,
    },
    output: {
        icon: 'spark',
        headline: 'Nog geen outputresultaten',
        subtext: 'Er zijn nog geen formulieren ingevuld voor deze output.',
        cta: TO_EVENTS,
    },
    outputIndicator: {
        icon: 'spark',
        headline: 'Nog geen outputresultaten',
        subtext: 'Er zijn nog geen formulieren ingevuld voor deze output.',
        cta: TO_EVENTS,
    },
    activity: {
        icon: 'spark',
        headline: 'Nog geen resultaten voor deze activiteit',
        subtext: 'Registreer outputs of vul formulieren in om resultaten te zien.',
        cta: TO_EVENTS,
    },

    // ── Static cards (staticCardRegistry types) ──
    agenda: {
        icon: 'clock',
        headline: 'Geen taken gepland',
        subtext: 'Er staan geen taken in deze periode.',
        cta: { label: 'Plan een taak', path: 'Agenda' },
    },
    'personas-status': {
        icon: 'chart',
        headline: 'Nog geen medewerkers',
        subtext: 'Voeg medewerkers toe om de statusverdeling te zien.',
        cta: { label: 'Voeg medewerker toe', path: 'personas' },
    },
    'personas-routes': {
        icon: 'chart',
        headline: 'Nog geen trajecten toegewezen',
        subtext: 'Wijs medewerkers toe aan een traject.',
        cta: TO_PERSONAS,
    },
    'personas-presence': {
        icon: 'clock',
        headline: 'Nog geen aanwezigheid geregistreerd',
        subtext: 'Registreer aanwezigheid om percentages te zien.',
        cta: TO_PERSONAS,
    },
    'personas-reports': {
        icon: 'clock',
        headline: 'Nog geen rapporten',
        subtext: 'Maak een rapport aan voor een medewerker.',
        cta: TO_PERSONAS,
    },
    'housevisits-status': {
        icon: 'chart',
        headline: 'Nog geen huisbezoeken',
        subtext: 'Registreer een huishouden om de status te volgen.',
        cta: TO_HOUSEVISITS,
    },
    'housevisits-hours': {
        icon: 'chart',
        headline: 'Nog geen uren geregistreerd',
        subtext: 'Registreer uren op huisbezoek-taken.',
        cta: TO_HOUSEVISITS,
    },
    'portfolio-housevisits-status': {
        icon: 'chart',
        headline: 'Geen projecten met huisbezoeken',
        subtext: 'Geen actieve projecten met huisbezoeken in scope.',
        cta: MANAGE_PROJECTS,
    },
    'portfolio-housevisits-hours': {
        icon: 'chart',
        headline: 'Geen projecten met huisbezoeken',
        subtext: 'Geen actieve projecten met huisbezoeken in scope.',
        cta: MANAGE_PROJECTS,
    },
    // De kaart toont alleen projecten met een ster (src/helpers/portfolio/projectMarks.js).
    'portfolio-projects': {
        icon: 'chart',
        headline: 'Nog geen projecten met een ster',
        subtext: 'Geef een project een ster in het portfolio-overzicht, dan staat het hier.',
        cta: { label: 'Naar het overzicht', path: 'projectsOverview' },
    },
    'open-project-tasks': {
        icon: 'clock',
        headline: 'Geen openstaande afspraken',
        subtext: 'Afspraken uit een overleg leg je vast op de detailpagina van een project.',
        cta: MANAGE_PROJECTS,
    },
    // AI-CONTEXT: See docs/features/update-aanvragen.md
    'update-requests': {
        icon: 'clock',
        headline: 'Nog geen updateverzoeken',
        subtext: 'Organisaties dienen die zelf in via de Updates-pagina in hun eigen omgeving.',
        cta: null,
    },
    // AI-CONTEXT: See docs/features/decca-gesprekken-overzicht.md
    'decca-conversations': {
        icon: 'clock',
        headline: 'Nog geen gesprekken met Decca',
        subtext: 'Zodra een organisatie Decca een vraag stelt, verschijnt het gesprek hier.',
        cta: null,
    },
    // AI-CONTEXT: See docs/features/arbeidsvaardigheden.md
    'work-skills-growth': {
        icon: 'clock',
        headline: 'Nog geen tweede beoordeling',
        subtext: 'Groei is pas te zien zodra een deelnemer twee keer is beoordeeld op arbeidsvaardigheden.',
        cta: TO_PERSONAS,
    },
    'msi-benchmarks': {
        icon: 'chart',
        headline: 'Nog geen benchmarkgegevens',
        subtext: 'Benchmarks verschijnen zodra er voldoende metingen zijn.',
        cta: null,
    },
}

export const GENERIC_EMPTY_STATE = {
    icon: 'chart',
    headline: 'Nog geen gegevens',
    subtext: 'Gegevens verschijnen zodra ze beschikbaar zijn.',
    cta: null,
}

// Shown when an active filter yields no results (distinct from "no data yet").
// The CTA has no `path` — the card supplies an `onCtaClick` that clears the filter.
export const FILTER_EMPTY_STATE = {
    icon: 'chart',
    headline: 'Geen resultaten voor dit filter',
    subtext: 'Pas het filter aan of wis het om alle gegevens te zien.',
    cta: { label: 'Wis filter' },
}

/**
 * Resolve the empty-state copy + CTA for a card type.
 *
 * @param {string} [type] - Card type (analytics type or static card key).
 * @returns {{ icon: string, headline: string, subtext: string, cta: ({ label: string, path: string } | null) }}
 */
export const getEmptyStateConfig = (type) =>
    EMPTY_STATE_CONFIG[type] || GENERIC_EMPTY_STATE
